import { documentRequestApi } from "@api/documentRequests";
import { documentApi } from "@api/documents";
import { fileApi } from "@api/files";
import { withCancel } from "@api/utils";
import { http } from "@api/base";
import { Error } from "@components/Error";
import { Loader } from "@components/Loader";
import { invenioConfig } from "@config";
import { RJSForm } from "@forms/rjsf";
import { goTo } from "@history";
import { BackOfficeRoutes } from "@routes/urls";
import _get from "lodash/get";
import _merge from "lodash/merge";
import PropTypes from "prop-types";
import React, { Component } from "react";
import { Message, Button, Icon } from "semantic-ui-react";
import { schema } from "./schema";
import { uiSchema } from "./uiSchema";
import MARC21Preview from "@components/MARC21Preview";

const ERROR_DISPLAY_DURATION = 5000;

export class DocumentEditor extends Component {
  constructor(props) {
    super(props);

    let prefilledFormData = {};

    if (!this.isEditing) {
      // Try location state first
      prefilledFormData = _get(props, "location.state.formData", {});

      // If empty, check sessionStorage for MARC21 import
      if (Object.keys(prefilledFormData).length === 0) {
        const storedData = sessionStorage.getItem("marc21ImportData");
        if (storedData) {
          try {
            prefilledFormData = JSON.parse(storedData);
            sessionStorage.removeItem("marc21ImportData"); // Clean up immediately
          } catch (e) {
            console.error("Failed to parse MARC21 data:", e);
          }
        }
      }
    }

    this.state = {
      data: { metadata: prefilledFormData },
      isLoading: this.isEditing,
      error: {},
      callNumberError: null,
      showFloatingError: false,
      formKey: 0,
      pendingCoverFile: null,
      hasMarcData: Object.keys(prefilledFormData).length > 0,
    };

    this.cancellableOperations = [];
    this.errorTimeout = null;
  }

  componentDidMount() {
    const { match } = this.props;
    if (match.params.documentPid) {
      this.fetch(match.params.documentPid);
    }
  }

  componentWillUnmount() {
    this.cancellableOperations.forEach((op) => op?.cancel());
    if (this.errorTimeout) {
      clearTimeout(this.errorTimeout);
    }
  }

  get isEditing() {
    const { match } = this.props;
    return !!match.params.documentPid;
  }

  get documentPid() {
    return this.props.match.params.documentPid;
  }

  addCancellableOperation = (operation) => {
    this.cancellableOperations.push(operation);
    return operation;
  };

  fetch = async (documentPid) => {
    const operation = this.addCancellableOperation(
      withCancel(documentApi.get(documentPid))
    );
    try {
      const response = await operation.promise;
      this.setState({ data: response.data, isLoading: false, error: {} });
    } catch (error) {
      if (error !== "UNMOUNTED") {
        this.setState({ isLoading: false, error });
      }
    }
  };

  buildFullCallNumber = (formData) => {
    const callNumber = formData["call_number"];
    const callNumberPrefix = formData["call_number_prefix"];

    if (!callNumber) return null;

    let fullCallNumber = callNumber;

    if (callNumberPrefix) {
      fullCallNumber = `${callNumberPrefix} ${fullCallNumber}`;
    }

    return fullCallNumber;
  };

  checkCallNumberUniqueness = async (formData, currentDocumentPid = null) => {
    const fullCallNumber = this.buildFullCallNumber(formData);

    if (!fullCallNumber) {
      return {
        isUnique: true,
        existingDocuments: [],
      };
    }

    const operation = this.addCancellableOperation(
      withCancel(
        documentApi.list(`call_number:"${fullCallNumber}"`, { size: 10 })
      )
    );

    const response = await operation.promise;
    const existingDocuments = response.data.hits;

    const duplicates = currentDocumentPid
      ? existingDocuments.filter(
          (doc) => doc.metadata.pid !== currentDocumentPid
        )
      : existingDocuments;

    return {
      isUnique: duplicates.length === 0,
      existingDocuments: duplicates,
    };
  };

  handleCoverFileSelected = (file) => {
    this.setState({ pendingCoverFile: file });
  };

  handleMARC21Save = async (marcFields) => {
    try {
      // Send MARC fields to backend for conversion
      const response = await http.post("/marc21/convert-to-document", {
        marc_fields: marcFields,
        document_pid: this.documentPid,
      });

      const convertedData = response.data.data;

      this.setState((prevState) => {
        const currentMetadata = prevState.data.metadata || {};

        // List of fields that MARC21 can control
        const marcControlledFields = [
          "title",
          "alternative_titles",
          "authors",
          "other_authors",
          "edition",
          "imprint",
          "publication_year",
          "publisher",
          "publication_place",
          "physical_description",
          "number_of_pages",
          "identifiers",
          "subjects",
          "abstract",
          "note",
          "series",
          "languages",
          "source",
          "table_of_content",
          "alternative_abstracts",
          "conference_info",
          "publication_info",
          "dimensions",
        ];

        const mergedMetadata = { ...currentMetadata };

        marcControlledFields.forEach((field) => {
          if (field in convertedData) {
            mergedMetadata[field] = convertedData[field];
          } else if (field in currentMetadata) {
            delete mergedMetadata[field];
          }
        });

        return {
          data: {
            ...prevState.data,
            metadata: mergedMetadata,
          },
          formKey: prevState.formKey + 1,
        };
      });
    } catch (error) {
      alert(
        "Failed to save MARC21 changes: " +
          (error.response?.data?.error || error.message)
      );
    }
  };

  uploadPendingCover = async (documentPid, coverFile) => {
    const operation = this.addCancellableOperation(
      withCancel(fileApi.cover.upload(documentPid, coverFile))
    );

    const response = await operation.promise;

    if (response?.data) {
      return {
        cover_metadata: response.data.cover_metadata,
        cover_urls: response.data.cover_urls,
      };
    }

    return null;
  };

  showFloatingError = (errorMessage) => {
    this.setState({
      callNumberError: errorMessage,
      showFloatingError: true,
    });

    this.errorTimeout = setTimeout(() => {
      this.setState({ showFloatingError: false });
    }, ERROR_DISPLAY_DURATION);
  };

  hideFloatingError = () => {
    this.setState({ showFloatingError: false });
    if (this.errorTimeout) {
      clearTimeout(this.errorTimeout);
    }
  };

  handleFormChange = (formData) => {
    if (formData.pending_cover?.file) {
      this.setState({ pendingCoverFile: formData.pending_cover.file });
      delete formData.pending_cover;
    } else if (formData.pending_cover === null) {
      this.setState({ pendingCoverFile: null });
      delete formData.pending_cover;
    } else if (formData.pending_cover) {
      delete formData.pending_cover;
    }

    this.setState((prevState) => ({
      data: { ...prevState.data, metadata: formData },
      callNumberError: null,
      showFloatingError: false,
    }));
  };

  submitAction = async (formData) => {
    const callNumber = formData["call_number"]?.trim();

    // Only check uniqueness if call number is provided - NO FORMAT VALIDATION
    if (callNumber) {
      const fullCallNumber = this.buildFullCallNumber(formData);
      if (fullCallNumber) {
        const uniquenessCheck = await this.checkCallNumberUniqueness(
          formData,
          this.documentPid
        );

        if (!uniquenessCheck.isUnique) {
          this.showFloatingError(
            `Call number "${fullCallNumber}" already exists. Please use a different call number.`
          );
          return Promise.reject({ skipFormError: true });
        }
      }
    }

    this.setState({
      callNumberError: null,
      showFloatingError: false,
    });

    delete formData._tooManyAuthorsCustomField;
    delete formData.pending_cover;
    delete formData.cover_preview_url;
    delete formData.cover_metadata_preview;

    const result = this.isEditing
      ? await documentApi.update(this.documentPid, formData)
      : await documentApi.create(formData);

    return result;
  };

  attachCreatedDocumentToDocumentRequest = async (
    documentPid,
    documentRequestPid
  ) => {
    const operation = this.addCancellableOperation(
      withCancel(
        documentRequestApi.addDocument(documentRequestPid, {
          document_pid: documentPid,
        })
      )
    );
    await operation.promise;
  };

  successCallback = async (response) => {
    const { location } = this.props;

    const newDocumentPid = response.data.metadata.pid;
    const extraData = _get(location, "state.extraData", {});
    const shouldAttachCreatedDocumentToDocumentRequest = _get(
      extraData,
      "attachCreatedDocumentToDocumentRequest",
      false
    );

    const { pendingCoverFile } = this.state;
    if (!this.isEditing && pendingCoverFile) {
      try {
        const coverData = await this.uploadPendingCover(
          newDocumentPid,
          pendingCoverFile
        );
        if (coverData) {
          const operation = this.addCancellableOperation(
            withCancel(documentApi.update(newDocumentPid, coverData))
          );
          await operation.promise;
        }
      } catch (error) {
        this.setState({
          error: {
            message:
              "Document created successfully, but cover upload failed. You can add a cover by editing the document.",
            details: error.message,
          },
        });
      }
    }

    // Handle document updates for selected documents
    if (this.isEditing && response && response.data) {
      // Store essential update data in sessionStorage
      try {
        const essentialData = {
          pid: response.data.pid,
          id: response.data.id,
          call_number: response.data.metadata?.call_number,
          title: response.data.metadata?.title,
          metadata: {
            call_number: response.data.metadata?.call_number,
            title: response.data.metadata?.title,
          },
        };

        sessionStorage.setItem(
          "lastUpdatedDocument",
          JSON.stringify({
            timestamp: Date.now(),
            document: essentialData,
          })
        );
      } catch (storageError) {
        console.warn("Could not store update in sessionStorage:", storageError);
      }

      window.dispatchEvent(
        new CustomEvent("documentUpdated", {
          detail: response.data,
        })
      );
    }

    // Navigation logic
    if (shouldAttachCreatedDocumentToDocumentRequest) {
      const documentRequestPid = extraData.documentRequestPid;
      await this.attachCreatedDocumentToDocumentRequest(
        newDocumentPid,
        documentRequestPid
      );
      goTo(BackOfficeRoutes.documentRequestDetailsFor(documentRequestPid));
    } else {
      goTo(BackOfficeRoutes.documentDetailsFor(newDocumentPid));
    }
  };

  getUISchemaOptions = () => {
    const { data } = this.state;
    const tooManyAuthors =
      data.metadata?.authors?.length >
      invenioConfig.DOCUMENTS.authors.maxEditable;

    if (tooManyAuthors && data.metadata) {
      data.metadata._tooManyAuthorsCustomField =
        data.metadata.authors
          .map((author) => author.full_name)
          .slice(0, invenioConfig.DOCUMENTS.authors.maxEditable)
          .join(", ") + " ...";
    }

    return {
      tooManyAuthors,
      documentPid: this.isEditing ? this.documentPid : null,
      isCreating: !this.isEditing,
      onCoverFileSelected: this.handleCoverFileSelected,
    };
  };

  renderFloatingError = () => {
    const { callNumberError, showFloatingError } = this.state;

    if (!callNumberError || !showFloatingError) return null;

    return (
      <div
        style={{
          position: "fixed",
          top: "20px",
          right: "20px",
          zIndex: 1000,
          maxWidth: "400px",
          animation: "slideIn 0.3s ease-out",
        }}
      >
        <Message
          negative
          onDismiss={this.hideFloatingError}
          header="Call Number Error"
          content={callNumberError}
        />
        <style jsx>
          {`
            @keyframes slideIn {
              from {
                transform: translateX(100%);
                opacity: 0;
              }
              to {
                transform: translateX(0);
                opacity: 1;
              }
            }
          `}
        </style>
      </div>
    );
  };

  renderForm = (formTitle, formData, keyPrefix, hasMarcData = false) => {
    const { formKey } = this.state;
    return (
      <>
        {this.isEditing && this.documentPid && (
          <div style={{ marginBottom: "20px", textAlign: "right" }}>
            <MARC21Preview
              documentId={this.documentPid}
              onSave={this.handleMARC21Save}
              isEditMode
              trigger={
                <Button
                  color="yellow"
                  icon
                  labelPosition="left"
                  style={{ color: "black" }}
                >
                  <Icon name="file code outline" />
                  View/Edit MARC21
                </Button>
              }
            />
          </div>
        )}

        <RJSForm
          key={`${keyPrefix}-${formKey}`}
          schema={schema()}
          uiSchema={uiSchema(formTitle, this.getUISchemaOptions())}
          formData={formData}
          submitAction={this.submitAction}
          successCallback={this.successCallback}
          successMessage={`The document was successfully ${
            this.isEditing ? "updated" : "created"
          }.`}
          onChange={this.handleFormChange}
          onCoverFileSelected={this.handleCoverFileSelected}
          isDocumentForm
          hasMarcData={hasMarcData}
        />
      </>
    );
  };

  render() {
    const { isLoading, error, data, callNumberError } = this.state;

    const displayError = callNumberError ? {} : error;

    return (
      <div style={{ position: "relative" }}>
        {this.renderFloatingError()}

        {this.isEditing ? (
          <Loader isLoading={isLoading}>
            <Error error={displayError}>
              {this.renderForm(
                `Document - Edit #${this.documentPid}`,
                data.metadata || {},
                `edit-${this.documentPid}`
              )}
            </Error>
          </Loader>
        ) : (
          <Error error={displayError}>
            {this.renderForm(
              this.isEditing
                ? `Document - Edit #${this.documentPid}`
                : "Document - Create",
              _merge(
                {},
                _get(this.props, "location.state.formData", {}),
                data.metadata
              ),
              "create",
              this.state.hasMarcData
            )}
          </Error>
        )}
      </div>
    );
  }
}

DocumentEditor.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      documentPid: PropTypes.string,
    }),
  }).isRequired,
  location: PropTypes.shape({
    state: PropTypes.shape({
      formData: PropTypes.object,
      extraData: PropTypes.object,
    }),
  }),
};

DocumentEditor.defaultProps = {
  location: { state: null },
};
